import type { FunctionComponent } from 'react'

import GetAppIcon from '@material-ui/icons/GetApp'
import {
	PrimaryButton,
	RegularButtonWrapper,
	TertiaryButton,
} from '@monthio/react-components'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

const AppIcon = styled(GetAppIcon)`
	margin-right: 16px;
`

interface DownloadButtonProps {
	fetchBlob: () => Promise<string>
	startDownloadText: string
	finishedDownloadText?: string
	fileName: string
	failedDownloadText?: string
	retries?: number
	retryDelayMS?: number
	children?: never
}

export const DownloadButton: FunctionComponent<DownloadButtonProps> = (
	props,
) => {
	const [fileUrl, setFileUrl] = useState<string | null>(null)
	const [shouldFetchBlob, setShouldFetchBlob] = useState<boolean>(false)
	const [loadingLoops, setLoadingLoops] = useState(0)
	const [isErrorState, setIsErrorState] = useState(false)
	const retries = props.retries ? props.retries : 5
	const retryDelayMS = props.retryDelayMS ? props.retryDelayMS : 2500

	let buttonText = props.startDownloadText

	useEffect(() => {
		const fetchBlob = async () => {
			if (fileUrl || !shouldFetchBlob) {
				return
			}

			if (loadingLoops > retries) {
				if (props.failedDownloadText) {
					buttonText = props.failedDownloadText
				}
				setIsErrorState(true)
				return
			}

			try {
				const url = await props.fetchBlob()
				if (url != null) {
					setFileUrl(url)
					if (props.finishedDownloadText) {
						buttonText = props.finishedDownloadText
					}
				}
			} catch (_error) {
				await new Promise((resolve) => setTimeout(resolve, retryDelayMS))
				setLoadingLoops(loadingLoops + 1)
			}
		}
		fetchBlob()
		if (fileUrl && shouldFetchBlob) {
			downloadfile(fileUrl, props.fileName)
			setShouldFetchBlob(false)
		}
	}, [loadingLoops, shouldFetchBlob, fileUrl])

	const Button = fileUrl ? TertiaryButton : PrimaryButton

	return (
		<RegularButtonWrapper>
			<Button
				loading={!isErrorState && shouldFetchBlob}
				onClick={() => setShouldFetchBlob(true)}
			>
				<AppIcon />
				{buttonText}
			</Button>
		</RegularButtonWrapper>
	)
}

const downloadfile = async (
	fileUrl: string,
	fileName: string,
): Promise<void> => {
	const link = document.createElement('a')
	link.href = fileUrl
	link.setAttribute('download', fileName)
	link.setAttribute('target', '_blank')
	document.body.appendChild(link)
	// Start download
	if (fileName.endsWith('.pdf') && navigator.userAgent.includes('Firefox')) {
		// Fixes firefox pdf handling
		open(fileUrl, '_blank')
	} else {
		link.click()
	}
	// Clean up and remove the link
	link.parentNode?.removeChild(link)
}
